/**
 * Invitations: the claim centre and the coverage card.
 *
 * The body of the Invitations screen (pages/AdminInvitations.tsx) since Sep
 * 17 2026, when Thomas moved it off the scrolling dashboard; until then it
 * was the dashboard's fifth section, RosterPanel. The page renders the
 * heading; this renders what is under it.
 *
 * It exists because P3's central unsolved problem is that most students on
 * the platform have not named an institution, and a university that opens a
 * dashboard showing a fraction of their students is going to ask what is
 * wrong. Answering that question inside the product, with the next action
 * attached, is the difference between a dashboard and a tool.
 *
 * The coverage rate here is the only figure on the entire surface whose
 * denominator P3 did not compute. It is the partner's own enrollment estimate,
 * and it is labelled as theirs on the card, in the footnote and in the trace
 * drawer. When no estimate has been entered the card shows the count and no
 * rate. It does not guess.
 */

import { PeopleGlyph } from "~/app/components/marks";
import {
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Denominator,
  LeadFigure,
  ProvenanceNote,
  StateCard,
  SubGroupLabel,
} from "~/app/components/primitives";
import { formatCount } from "~/app/lib/format";
import type { CohortView } from "~/app/lib/demo-data";
import { ClaimCentre } from "~/app/panels/ClaimCentre";
import { RankedList, SERIES } from "~/app/panels/panel-charts";

export function InvitationsPanel({ view }: { view: CohortView }) {
  const { institution, coverage } = view;

  return (
    <>
      <ClaimCentre institution={institution} />

      <SubGroupLabel>Who has arrived</SubGroupLabel>

      <div className="e-grid">
        {/* ONE card, not two (Thomas, Sep 9 2026): the coverage figure and
            its ring sat in a two-column card that was mostly empty beside
            the channel list that explains it. The figure, the ring and how
            students joined now share a row, with one footnote carrying both
            provenances. */}
        <Card span="full">
          <CardHeader
            aside={
              <span className="e-kpi-tile hue-1" aria-hidden="true">
                <PeopleGlyph />
              </span>
            }
          >
            <CardTitle sub="Claimed students against your enrollment estimate, and how they joined.">
              Roster coverage
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div
              className={
                coverage.available && coverage.estimate !== null
                  ? "e-coverage"
                  : "e-coverage is-no-estimate"
              }
            >
              {coverage.available && coverage.estimate !== null ? (
                <>
                  {/* The rate as a line under the figure (Thomas, Sep 9 2026): the
                      ring's legend read against the rail's "seats used", which has a
                      different denominator. The line names its own. */}
                  <div className="e-vstack">
                    <LeadFigure
                      cell={coverage.claimed}
                      format={formatCount}
                      label="Students claimed"
                      provenance={coverage.provenance}
                    />
                    <Denominator
                      cell={coverage.rate}
                      of="of the students your team estimated have claimed you"
                      noun="students your team estimated"
                      label="Roster coverage"
                    />
                  </div>
                </>
              ) : (
                <StateCard
                  variant="not-measured"
                  title="No enrollment figure on file"
                  action={
                    <p className="e-note">
                      Send one to your P3 contact and this becomes a rate.
                    </p>
                  }
                >
                  A coverage rate needs a denominator, and P3 cannot see your
                  enrollment records. Until an estimate is on file, this card
                  shows the count and no percentage rather than guessing at one.
                </StateCard>
              )}
              <div className="e-coverage-channels" aria-labelledby="coverage-channels">
                <SubGroupLabel id="coverage-channels">How students joined</SubGroupLabel>
                {view.channels.allSuppressed ? (
                  <StateCard variant="building" title="No channel has reached ten yet">
                    Every joining route is under the reporting threshold on its own.
                    They fill in as your roster grows.
                  </StateCard>
                ) : (
                  <RankedList
                    distribution={view.channels}
                    label={`How students joined ${institution.legalName}, ${view.windowLabel}`}
                    color={SERIES[1]}
                  />
                )}
              </div>
            </div>
          </CardBody>
          <CardFootnote>
            <ProvenanceNote provenance={coverage.provenance} />
            <ProvenanceNote
              provenance={view.channels.provenance}
              suppressed={view.channels.hiddenCount > 0}
            />
          </CardFootnote>
        </Card>
      </div>
    </>
  );
}

